import { Box, Divider, Stack } from "@mui/material";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import { useHotkeyContext } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { useMaterials } from "hooks/useMaterials";
import { ReactNode, useMemo, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useScrapEyeMetadata } from "src/contexts/scrapEyeMetadata";
import { CommentModal } from "./CommentModal";
import { EventDetailsSection } from "./sections/EventDetailsSection";
import { LocationSection } from "./sections/LocationSection";
import { CommentSection } from "./sections/CommentSection";
import { MaterialSection } from "./sections/MaterialSection";
import { ActionButtonsSection } from "./sections/ActionButtonsSection";
import { SelectMaterialModal } from "./SelectMaterialModal";
import { EditEventHotkeys, HotkeyLabelOverrides } from "./EditEventHotkeys";

interface EventEditControlsProps {
  event: ScrapEyesEvent;
  location: string;
  onChange: (updates: Partial<ScrapEyesEvent>) => void;
  scope: string;
  enabled: boolean;
  onOpenAreaModal?: () => void;
  onSave?: () => void;
  saveBusy?: boolean;
  onDelete?: () => void;
  showNavigation?: boolean;
  showHotkeyLegend?: boolean;
  hotkeyLabels?: HotkeyLabelOverrides;
  massSection?: ReactNode;
  connectedEventsCheckbox?: ReactNode;
  showCommentAsModal?: boolean;
  useGridLayout?: boolean;
}

export const EventEditControls = ({
  event,
  location,
  onChange,
  scope,
  enabled,
  onOpenAreaModal,
  onSave,
  saveBusy = false,
  onDelete,
  showNavigation = false,
  showHotkeyLegend = true,
  hotkeyLabels,
  massSection,
  connectedEventsCheckbox,
  showCommentAsModal = false,
  useGridLayout = false,
}: EventEditControlsProps) => {
  const { materials } = useMaterials();
  const { getDisplayKey, getHotkey } = useHotkeyContext();
  const { available_areas: availableAreas } = useScrapEyeMetadata();
  const [materialModalOpen, setMaterialModalOpen] = useState(false);
  const [coMaterialModalOpen, setCoMaterialModalOpen] = useState(false);
  const [commentModalOpen, setCommentModalOpen] = useState(false);

  const filteredMaterials = useMemo(() => {
    return (
      materials?.filter((material) => material.labelling_name !== null) ?? []
    );
  }, [materials]);

  const changeMaterialKey = getHotkey("changeMaterialMode");
  useHotkeys(
    changeMaterialKey,
    () => {
      setMaterialModalOpen(true);
    },
    {
      enabled: enabled && event.eventType !== "flag",
      enableOnFormTags: false,
      preventDefault: true,
      scopes: [scope],
    },
    [enabled, event.eventType, changeMaterialKey]
  );

  const changeCoMaterialKey = getHotkey("changeCoMaterialMode");
  useHotkeys(
    changeCoMaterialKey,
    () => {
      setCoMaterialModalOpen(true);
    },
    {
      enabled: enabled && event.eventType !== "flag" && event.material != null,
      enableOnFormTags: false,
      preventDefault: true,
      scopes: [scope],
    },
    [enabled, event.eventType, event.material, changeCoMaterialKey]
  );

  const changeAreaKey = getHotkey("changeAreaMode");
  useHotkeys(
    changeAreaKey,
    () => {
      if (onOpenAreaModal) {
        onOpenAreaModal();
      }
    },
    {
      enabled: enabled && !!onOpenAreaModal && event.eventType !== "flag",
      enableOnFormTags: false,
      preventDefault: true,
      scopes: [scope],
    },
    [enabled, onOpenAreaModal, event.eventType, changeAreaKey]
  );

  // Save and delete hotkeys
  useHotkeys(
    "mod+enter",
    () => {
      if (onSave) {
        onSave();
      }
    },
    {
      enabled: enabled && !!onSave && !saveBusy,
      enableOnFormTags: true,
      preventDefault: true,
      scopes: [scope],
    },
    [onSave, enabled, saveBusy]
  );

  const deleteKey = getHotkey("deleteEvent");
  useHotkeys(
    deleteKey,
    () => {
      if (onDelete) {
        onDelete();
      }
    },
    {
      enabled: enabled && !!onDelete,
      enableOnFormTags: false,
      preventDefault: true,
      scopes: [scope],
    },
    [deleteKey, onDelete, enabled]
  );

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        bgcolor: "background.paper",
        overflow: "auto",
      }}
    >
      <SelectMaterialModal
        open={materialModalOpen}
        onClose={() => {
          setMaterialModalOpen(false);
        }}
        event={event}
        filteredMaterials={filteredMaterials}
        onChange={onChange}
        targetField="material"
      />

      <SelectMaterialModal
        open={coMaterialModalOpen}
        onClose={() => {
          setCoMaterialModalOpen(false);
        }}
        event={event}
        filteredMaterials={filteredMaterials}
        onChange={onChange}
        targetField="coMaterial"
        excludeMaterialName={event.material}
      />

      {showCommentAsModal ? (
        <CommentModal
          open={commentModalOpen}
          onClose={() => {
            setCommentModalOpen(false);
          }}
          event={event}
          onChange={onChange}
        />
      ) : null}

      <EventDetailsSection
        event={event}
        onOpenCommentModal={
          showCommentAsModal
            ? () => {
                setCommentModalOpen(true);
              }
            : undefined
        }
      />
      {showCommentAsModal ? null : (
        <CommentSection event={event} onChange={onChange} />
      )}

      <Divider />

      {event.eventType !== "flag" && (
        <>
          <Box
            sx={
              useGridLayout
                ? {
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 2,
                  }
                : { display: "flex", gap: 4 }
            }
          >
            <LocationSection
              event={event}
              availableAreas={availableAreas}
              location={location}
              onChange={onChange}
              onOpenAreaModal={onOpenAreaModal}
            />

            <MaterialSection
              event={event}
              filteredMaterials={filteredMaterials}
              targetField="material"
              onOpenModal={() => {
                setMaterialModalOpen(true);
              }}
            />

            {event.material != null ? (
              <MaterialSection
                event={event}
                filteredMaterials={filteredMaterials}
                targetField="coMaterial"
                onOpenModal={() => {
                  setCoMaterialModalOpen(true);
                }}
              />
            ) : null}

            {massSection}
          </Box>

          {connectedEventsCheckbox}

          <Divider />
        </>
      )}

      <ActionButtonsSection
        onSave={onSave}
        saveBusy={saveBusy}
        onDelete={onDelete}
        getDisplayKey={getDisplayKey}
      />

      {showHotkeyLegend ? (
        <>
          <Divider />

          <EditEventHotkeys
            showAreaAndMaterial={event.eventType !== "flag"}
            showDelete={onDelete != null}
            showNavigation={showNavigation}
            hotkeyLabels={hotkeyLabels}
          />
        </>
      ) : null}
    </Stack>
  );
};
